import { DEV_MODE } from "Constant/app/process";

import { FailedLead } from "./FailedLeadsModal.types";

/**
 * Dev only stand in for the failed / duplicate leads endpoints, mirroring the
 * Facebook flow in `components/Leads/LeadsStep1` where `DEV_MODE` swaps the API
 * response for a locally built one. The callers only fall back to this when the
 * real request came back empty, so actual data always wins.
 */
export interface DevMockLeadsResponse {
    status: true;
    data: {
        leads: FailedLead[];
        pagination: {
            current_page: number;
            per_page: number;
            total: number;
            last_page: number;
        };
    };
}

const DAY_MS = 24 * 60 * 60 * 1000;

const toApiDate = (date: Date): string =>
    date.toISOString().slice(0, 19).replace("T", " ");

/**
 * Each entry covers a different branch of the card UI: retryable, retry limit
 * reached, already scheduled, JSON body, short message, long message (so the
 * reason gets truncated and the expand toggle shows up) and no response at all.
 */
const buildDevMockFailedLeads = (now: number): FailedLead[] => [
    {
        stat_id: 910001,
        lead_type: "Facebook Lead Ads",
        total_retries: 0,
        last_retried_at: null,
        last_status: "SEND_FAILED",
        retry_available: true,
        scheduled: false,
        last_response: {
            response: {
                code: 422,
                message: "Mock: required field `email` is missing.",
            },
        },
    },
    {
        stat_id: 910002,
        lead_type: "Facebook Lead Ads",
        total_retries: 2,
        last_retried_at: toApiDate(new Date(now - DAY_MS)),
        last_status: "SEND_FAILED",
        retry_available: true,
        scheduled: false,
        last_response: {
            response: {
                code: 400,
                data: {
                    error: "invalid_payload",
                    details: [
                        "Mock: `phone` must be in E.164 format.",
                        "Mock: `consent` must be a boolean.",
                    ],
                },
            },
        },
    },
    {
        stat_id: 910003,
        lead_type: "TikTok Lead Generation",
        total_retries: 1,
        last_retried_at: toApiDate(new Date(now - 2 * DAY_MS)),
        last_status: "PROCESS_FAILED",
        retry_available: true,
        scheduled: true,
        last_response: {
            response: {
                code: 500,
                message:
                    "Mock: the destination returned an internal server error while creating the contact. This message is intentionally long so the reason gets truncated and the expand toggle becomes visible.",
            },
        },
    },
    {
        stat_id: 910004,
        lead_type: "Source API",
        total_retries: 5,
        last_retried_at: toApiDate(new Date(now - 3 * DAY_MS)),
        last_status: "SEND_FAILED",
        retry_available: false,
        scheduled: false,
        last_response: {
            response: {
                code: 401,
                message: "Mock: destination token expired.",
            },
        },
    },
    {
        stat_id: 910005,
        lead_type: "LinkedIn Lead Gen Forms",
        total_retries: 0,
        last_retried_at: null,
        last_status: "FAILED_TO_FETCH_DATA",
        retry_available: true,
        scheduled: false,
        last_response: [],
    },
];

const buildDevMockDuplicateLeads = (now: number): FailedLead[] => [
    {
        stat_id: 920001,
        lead_type: "Facebook Lead Ads",
        total_retries: 0,
        last_retried_at: toApiDate(new Date(now - DAY_MS)),
        last_status: "DUPLICATED",
        retry_available: false,
        scheduled: false,
        last_response: {
            response: {
                code: 409,
                message: "Mock: a lead with this email already exists.",
            },
        },
    },
    {
        stat_id: 920002,
        lead_type: "Google Lead Forms",
        total_retries: 0,
        last_retried_at: toApiDate(new Date(now - 4 * DAY_MS)),
        last_status: "DUPLICATED",
        retry_available: false,
        scheduled: false,
        last_response: {
            response: { code: 409, message: "Mock: duplicate phone number." },
        },
    },
];

/**
 * Repeats the fixtures until there are enough rows to page through, keeping the
 * stat ids unique so selection and retry behave like the real list.
 */
const repeatLeads = (leads: FailedLead[], totalCount: number): FailedLead[] =>
    Array.from({ length: totalCount }, (_, index) => {
        const base = leads[index % leads.length];
        const cycle = Math.floor(index / leads.length);

        return cycle === 0
            ? base
            : { ...base, stat_id: base.stat_id + cycle * 100 };
    });

const buildResponse = (
    leads: FailedLead[],
    page: number,
    pageSize: number,
): DevMockLeadsResponse => {
    const safePageSize = pageSize > 0 ? pageSize : 10;
    const lastPage = Math.max(1, Math.ceil(leads.length / safePageSize));
    const currentPage = Math.min(Math.max(1, page), lastPage);
    const offset = (currentPage - 1) * safePageSize;

    return {
        status: true,
        data: {
            leads: leads.slice(offset, offset + safePageSize),
            pagination: {
                current_page: currentPage,
                per_page: safePageSize,
                total: leads.length,
                last_page: lastPage,
            },
        },
    };
};

const MOCK_FAILED_LEADS_TOTAL = 14;
const MOCK_DUPLICATE_LEADS_TOTAL = 6;

export const buildDevMockFailedLeadsResponse = (
    page = 1,
    pageSize = 10,
): DevMockLeadsResponse =>
    buildResponse(
        repeatLeads(
            buildDevMockFailedLeads(Date.now()),
            MOCK_FAILED_LEADS_TOTAL,
        ),
        page,
        pageSize,
    );

export const buildDevMockDuplicateLeadsResponse = (
    page = 1,
    pageSize = 10,
): DevMockLeadsResponse =>
    buildResponse(
        repeatLeads(
            buildDevMockDuplicateLeads(Date.now()),
            MOCK_DUPLICATE_LEADS_TOTAL,
        ),
        page,
        pageSize,
    );

/**
 * Wraps a failed / duplicate leads API response: in DEV_MODE an empty result is
 * replaced by the fixtures, so the badges, counters and the modal all have
 * something to render. Outside DEV_MODE, and whenever the backend actually
 * returned leads, the real response is passed straight through.
 */
export const withDevMockLeads = <T extends { status?: boolean; data?: any }>(
    response: T,
    buildMock: () => DevMockLeadsResponse,
): T | DevMockLeadsResponse => {
    if (!DEV_MODE) return response;
    if (response?.status && response?.data?.leads?.length) return response;

    return buildMock();
};
